import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2 } from "lucide-react";
import { driverOpenTrips, unfinishedBefore, clearDriverLoad } from "@/lib/driverLoad";
import { PendingWarning } from "@/components/portal/PendingWarning";
import { needsWheelchair } from "@/components/portal/AutoAssignDialog";

const darkField = "w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

function normalize(data) {
  const arr = Array.isArray(data) ? data : data?.replacements || data?.drivers || [];
  return arr.map((d) => ({
    id: d.id || d.driver_id,
    name: d.name || d.driver_name || d.full_name || "Driver",
    meta: d.recommendation || d.reason || d.note || d.status || "",
  })).filter((d) => d.id);
}

export default function ReassignDialog({ trip, open, onOpenChange, onReassigned }) {
  const [drivers, setDrivers] = useState([]);
  const [loadingDrivers, setLoadingDrivers] = useState(false);
  const [driverId, setDriverId] = useState("");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState("");
  const [pending, setPending] = useState([]);
  const [ack, setAck] = useState(false);
  const [checking, setChecking] = useState(false);
  const [vehByDriver, setVehByDriver] = useState({});
  const [allDrivers, setAllDrivers] = useState([]);
  const isNew = !trip?.assigned_driver_id && !trip?.assigned_driver_name;
  const wc = needsWheelchair(trip);
  const pickedVeh = vehByDriver[driverId];
  const wcMismatch = wc && driverId && !pickedVeh?.wheelchair_accessible
    ? (pickedVeh ? `${pickedVeh.fleet_number} isn't wheelchair accessible` : "this driver has no vehicle assigned") : "";

  useEffect(() => {
    setPending([]); setAck(false); setConflict(""); setChecking(false);
    if (!driverId || !trip) return;
    let alive = true;
    setChecking(true);
    driverOpenTrips(driverId).then((open) => { if (alive) { setPending(unfinishedBefore(open, trip)); setChecking(false); } });
    return () => { alive = false; };
  }, [driverId, trip]);

  useEffect(() => {
    if (!open || !trip) return;
    setDriverId(""); setReason(""); setConflict(""); setDrivers([]); setLoadingDrivers(true);
    fleetApi.get("/vehicles").then((r) => setVehByDriver(Object.fromEntries((r.data || []).filter((v) => v.current_driver_id).map((v) => [v.current_driver_id, v])))).catch(() => setVehByDriver({}));
    fleetApi.get("/drivers").then((r) => setAllDrivers((r.data || []).filter((d) => d.status !== "retired").map((d) => ({ id: d.id, name: d.full_name || d.name, meta: "" })))).catch(() => setAllDrivers([]));
    fleetApi.get(`/trips/${trip.id}/replacements`)
      .then((r) => { const n = normalize(r.data); if (n.length) return n; throw new Error("empty"); })
      .catch(() => fleetApi.get("/drivers").then((r) => normalize(r.data)).catch(() => []))
      .then((list) => setDrivers(list))
      .finally(() => setLoadingDrivers(false));
  }, [open, trip]);

  const submit = async () => {
    if (!driverId) { toast.error("Select a driver."); return; }
    if (checking) return;
    if (wcMismatch) { toast.error("This is a wheelchair trip — pick a driver marked ♿."); return; }
    if (pending.length && !ack) { toast.error("This driver has unfinished trips — tick “Add this trip to their list anyway” to continue."); return; }
    if (!isNew && !reason.trim()) { toast.error("Please enter a reason for the reassignment."); return; }
    setSaving(true);
    try {
      if (isNew) await fleetApi.post(`/trips/${trip.id}/assign`, { driver_id: driverId, reason: reason.trim(), override: !!conflict });
      else await fleetApi.post(`/trips/${trip.id}/reassign`, { driver_id: driverId, reason: reason.trim() });
      clearDriverLoad();
      const d = options.find((x) => String(x.id) === String(driverId));
      toast.success(`Trip ${isNew ? "assigned" : "reassigned"} to ${(d?.name || "driver").replace(/\.$/, "")}.`);
      onReassigned?.(trip.id, { assigned_driver_id: driverId, assigned_driver_name: d?.name || null });
      onOpenChange(false);
    } catch (e) {
      const status = e.response?.status;
      if (isNew && status === 409 && !conflict) { setConflict(fleetError(e, "This driver is off shift or has a conflict.")); return; }
      toast.error(status === 403 ? "You don't have permission to reassign trips."
        : fleetError(e, "Reassignment failed."));
    } finally { setSaving(false); }
  };

  if (!trip) return null;
  const options = wc
    ? [...allDrivers.filter((d) => vehByDriver[d.id]?.wheelchair_accessible && !drivers.some((x) => x.id === d.id)), ...drivers]
        .sort((a, b) => (vehByDriver[b.id]?.wheelchair_accessible ? 1 : 0) - (vehByDriver[a.id]?.wheelchair_accessible ? 1 : 0))
    : drivers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="border-slate-800 bg-[#0f172a] text-white" data-testid="reassign-dialog">
        <DialogHeader>
          <DialogTitle>{isNew ? "Assign trip" : "Reassign trip"}</DialogTitle>
          <p className="text-sm text-slate-400">
            {trip.external_trip_id} · {trip.rider_name} — currently {trip.assigned_driver_name || "unassigned"}
          </p>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">{isNew ? "Driver" : "New driver"}</label>
            {loadingDrivers ? (
              <div className="flex items-center gap-2 text-sm text-slate-400"><Loader2 className="h-4 w-4 animate-spin" /> Loading drivers…</div>
            ) : (
              <select value={driverId} onChange={(e) => setDriverId(e.target.value)} className={darkField} data-testid="reassign-driver-select">
                <option value="">Select a driver…</option>
                {options.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{vehByDriver[d.id]?.wheelchair_accessible ? " ♿" : ""}{wc && !vehByDriver[d.id]?.wheelchair_accessible ? " (no wheelchair vehicle)" : ""}{d.meta ? ` — ${d.meta}` : ""}</option>
                ))}
              </select>
            )}
          </div>
          <div>
            <label className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-slate-400">Reason{isNew ? " (optional)" : ""}</label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
              placeholder={isNew ? "Any note for the audit trail" : "Why is this trip being reassigned?"} data-testid="reassign-reason"
              className="border-slate-700 bg-slate-900 text-white placeholder:text-slate-500" />
          </div>
          {wc && (
            <p className={`rounded-md border px-3 py-2 text-xs ${wcMismatch ? "border-red-500/30 bg-red-500/10 text-red-200" : "border-blue-500/30 bg-blue-500/10 text-blue-200"}`} data-testid={wcMismatch ? "assign-wc-mismatch" : "assign-wc-note"}>
              {wcMismatch ? `Needs a wheelchair vehicle — ${wcMismatch}. Pick a driver marked ♿.` : "Wheelchair trip — only drivers with a wheelchair vehicle (♿) can take it."}
            </p>
          )}
          <PendingWarning pending={pending} ack={ack} onAck={setAck} testid="assign-pending-warning" />
          {conflict && (
            <p className="rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-xs text-amber-200" data-testid="assign-conflict">
              {conflict} Press Assign anyway to override.
            </p>
          )}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="reassign-cancel">Cancel</Button>
          <Button onClick={submit} disabled={saving || checking || !!wcMismatch} className="gap-2 bg-blue-700 text-white hover:bg-blue-800" data-testid="reassign-confirm">
            {saving || checking ? <Loader2 className="h-4 w-4 animate-spin" /> : null} {checking ? "Checking driver's trips…" : isNew ? (conflict ? "Assign anyway" : "Assign") : "Reassign"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
